import styles from './UploadTracker.module.css';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { UPLOAD_MANAGER } from '@he-novation/config/paths/modals.constants';
import { Icon } from '@he-novation/design-system/components/graphics/Icon/Icon';
import { ProgressCircle } from '@he-novation/design-system/components/widgets/ProgressCircle/ProgressCircle';
import {
    DirectionX,
    DirectionY,
    getAbsolutePosition
} from '@he-novation/design-system/utils/getAbsolutePosition';
import cn from 'classnames';
import { useTranslate } from '../../../hooks/useTranslate';

import type { FinishedUpload, PendingUpload, Upload } from '$helpers/Uploader.types';
import { useModal } from '$hooks/useModal';
import { useUploader } from '$hooks/useUploader';

export function UploadTracker() {
    const { openModal } = useModal();

    const { uploads, pending, finished, errors, uploadFolderCreations } = useUploader();
    const { total, uploaded } = computeProgress(uploads, pending, finished);
    const { t } = useTranslate();
    const triggerRef = useRef<HTMLDivElement>(null);
    const absoluteRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [displayFolderCreation, setDisplayFolderCreation] = useState<string | false>(false);

    useEffect(() => {
        if (uploadFolderCreations.total) {
            setDisplayFolderCreation(
                t('folder.Creating folders {{n}} / {{total}}', {
                    n: uploadFolderCreations.created,
                    total: uploadFolderCreations.total
                })
            );
            clearTimeout(timeoutRef.current);
        }
        if (uploadFolderCreations.created === uploadFolderCreations.total) {
            timeoutRef.current = setTimeout(() => {
                setDisplayFolderCreation(false);
            }, 3000);
        }

        if (!triggerRef.current || !absoluteRef.current) return;
        const style = getAbsolutePosition(triggerRef, absoluteRef, [
            DirectionX.Right,
            DirectionY.Middle
        ]);
        absoluteRef.current.style.left = style.left + 'px';
        absoluteRef.current.style.top = style.top + 'px';
        absoluteRef.current.style.width = style.width + 'px';
    }, [uploadFolderCreations.created, uploadFolderCreations.total]);

    if (!total && !finished.length && !errors.length && !displayFolderCreation) return null;
    return (
        <>
            <ProgressCircle
                ref={triggerRef}
                className={styles.tracker}
                total={total}
                value={uploaded}
                onClick={() => openModal(UPLOAD_MANAGER)}
            >
                <Icon icon="arrow_up" />
            </ProgressCircle>
            {displayFolderCreation &&
                ReactDOM.createPortal(
                    <div
                        ref={absoluteRef}
                        className={cn(
                            'bg-bg-2 text-s  border-border-1 border-1 text-text-faded pointer-events-none  absolute ml-10 rounded-xl px-10 py-5 shadow-lg backdrop-blur-xl'
                        )}
                    >
                        {displayFolderCreation}
                    </div>,
                    document.body
                )}
        </>
    );
}

function computeProgress(uploads: Upload[], pending: PendingUpload[], finished: FinishedUpload[]) {
    let uploaded = finished.reduce((n, { file }) => n + file.size, 0);
    let total = uploaded + pending.reduce((n, { file }) => n + file.size, 0);
    for (const { progression } of uploads) {
        total += progression.total;
        uploaded += progression.loaded;
    }

    return { total, uploaded };
}
